/*
 #*===================================================================
 #*
 #* Licensed Materials - Property of Zuum Transportation Inc.
 #*
 #* Copyright Zuum 2015-2022. All Rights Reserved.
 #*
 #*===================================================================
 */

import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';

// Services
import { ClientService } from '@core/services';
// Models
import { ApiResponse } from '@core/models';
// Store
import { ClientListActions } from '@clients-tms/shared/store';
import { UtilsActions } from '@main/store/actions';
import { SNACKBAR_NOTIFICATION_TYPE } from '@truckstop-tms/zuum-lib';

@Injectable()
export class ClientsTmsEffects {
  loadClients$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClientListActions.loadClients),
      switchMap(({ clientSearch }) => this.clientService.search(clientSearch)),
      map((response: ApiResponse) =>
        response.result.success
          ? ClientListActions.loadClientsSuccess({
              clients: response.data.clients,
              totalNumberOfClients: response.data.total,
            })
          : ClientListActions.loadClientsFailure({ errorMessage: response.result.message })
      ),
      catchError((error) => of(ClientListActions.loadClientsFailure({ errorMessage: error })))
    )
  );

  updateClientStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClientListActions.updateClientStatus),
      switchMap(({ client, status }) => this.clientService.updateClientStatus(client.id, status)),
      mergeMap((response) =>
        response.result.success
          ? [
              UtilsActions.showNotification({ data: { message: 'Status update successfully' } }),
              ClientListActions.updateClientStatusSuccess({ client: response.data }),
            ]
          : [
              UtilsActions.showNotification({
                data: { message: 'Status update failed', type: SNACKBAR_NOTIFICATION_TYPE.ERROR },
              }),
              ClientListActions.updateClientStatusFailure({ errorMessage: response.result.message }),
            ]
      )
    )
  );

  constructor(private actions$: Actions, private clientService: ClientService) {}
}
